import { Global, css, useTheme } from '@emotion/react';
import Select, { SelectProps } from 'rc-select';
import * as React from 'react';

import { ClearIcon } from '~src/icons';
import { fontStyles } from '~src/theme/fontStyles';

export interface SelectInputProps extends SelectProps {
  autoComplete?: string;
  name?: string;
  label?: string;
  error?: string | boolean;
  onBlur?: () => void;
  dataTest?: string;
  required?: boolean;
}

export const SelectInput: React.FC<SelectInputProps> = ({
  children,
  label,
  value,
  error,
  searchValue,
  dataTest,
  required,
  ...rest
}) => {
  const theme = useTheme();

  const labelElement = (
    <span
      style={{
        color: error ? theme.error35 : theme.neutral35,
      }}
      css={!!value ? shellStyle : placeholderStyle}
    >
      {typeof error === 'string' ? error : label}
    </span>
  );

  const select = (
    <Select
      value={value}
      placeholder={labelElement}
      showArrow={false}
      /* This is tricky but works to disable icon... */
      menuItemSelectedIcon={true}
      prefixCls="ingrid-select"
      className={error ? 'ingrid-select-has-error' : undefined}
      style={
        {
          color: 'black',
          ...fontStyles.px14_20,
        } as React.CSSProperties
      }
      dropdownStyle={
        {
          color: 'black',
          ...fontStyles.px16_20,
        } as React.CSSProperties
      }
      allowClear={Boolean(searchValue)}
      clearIcon={<ClearIcon color={theme.neutral85} />}
      searchValue={searchValue}
      data-test={dataTest}
      aria-invalid={!!error}
      aria-required={required}
      {...rest}
    >
      {children}
    </Select>
  );

  const styles = (
    <Global
      styles={css`
        .ingrid-select {
          height: 100%;
          width: 100%;
        }
        .ingrid-select-dropdown {
          z-index: 1000;
        }
        .ingrid-select-selector {
          border: 1px solid ${theme.neutral85};
          background-color: white;
          height: 60px;
          position: relative;
          cursor: pointer;
        }
        .ingrid-select-focused .ingrid-select-selector {
          border: 1px solid #000;
          height: 60px;
          position: relative;
          cursor: pointer;
        }
        .ingrid-select-has-error .ingrid-select-selector {
          border-color: #ee0000;
          height: 60px;
          position: relative;
          cursor: pointer;
        }
        .ingrid-select-selection-search-input {
          padding: 16px;
          border: none;
          height: 100%;
          width: 100%;
          outline: none;
          appearance: none;
          cursor: pointer;
        }
        .ingrid-select-selection-placeholder {
          position: absolute;
          left: 16px;
          top: 19px;
          height: 20px;
          color: ${theme.neutral35};
          font-size: 16px;
          line-height: 20px;
          text-wrap: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          width: calc(100% - 32px);
        }
        .ingrid-select-selection-item {
          position: absolute;
          left: 16px;
          top: 28px;
          text-wrap: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          width: calc(100% - 32px);
        }
        .ingrid-select-dropdown-hidden {
          display: none;
        }
        .ingrid-select-dropdown > div {
          background-color: white;
          border-width: 0 1px 1px 1px;
          border-color: ${theme.neutral85};
          border-style: solid;
          position: relative;
          top: -4px;
        }
        .ingrid-select-item {
          padding: 16px;
          border-bottom: 1px solid ${theme.neutral85};
          cursor: pointer;
        }
        .ingrid-select-item-option-active {
          background-color: ${theme.neutral99};
        }
        .ingrid-select-item-empty {
          padding: 16px;
          color: ${theme.neutral35};
          font-size: 12px;
          line-height: 16px;
          text-align: center;
        }
        input[type='search']::-webkit-search-cancel-button {
          -webkit-appearance: none;
          display: none;
        }
        .ingrid-select-clear {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 44px;
          height: 44px;
          position: absolute;
          top: 8px;
          right: 5px;
        }
      `}
    />
  );

  return (
    <>
      <div css={wrapperStyle}>
        {select}
        {!!value && labelElement}
      </div>
      {styles}
    </>
  );
};

const wrapperStyle = css({
  position: 'relative',
  height: '60px',
  width: '100%',
});

const shellStyle = css({
  position: 'absolute',
  top: '11px',
  left: '16px',
  textWrap: 'nowrap',
  textOverflow: 'ellipsis',
  ...fontStyles.px12_16,
});

const placeholderStyle = css({
  width: 'calc(100% - 32px)',
  ...fontStyles.px16_20,
});
